import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SubmitButton } from "@/components/ui/SubmitButton";
import { cn } from "@/utils/cn";

const schema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dob: z.string().min(1, "Date of birth is required"),
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
});

type FormData = z.infer<typeof schema>;

export function RequestPinPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", dob: "", email: "" },
  });

  const onSubmit = async (_data: FormData) => {
    toast.success("PIN request submitted.");
  };

  return (
    <section className="card card-featured mb-4">
      <div className="card-body">
        <span>
          <h3 className="label">Request PIN</h3>
        </span>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="pin-first" className="label f-weight-550">
              First name
            </label>
            <input
              id="pin-first"
              type="text"
              placeholder="First name"
              className={cn("form-control form-control-sm", errors.firstName && "border-red-500")}
              {...register("firstName")}
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="pin-last" className="label f-weight-550">
              Last name
            </label>
            <input
              id="pin-last"
              type="text"
              placeholder="Last name"
              className={cn("form-control form-control-sm", errors.lastName && "border-red-500")}
              {...register("lastName")}
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="pin-dob" className="label f-weight-550">
              Date of birth
            </label>
            <input
              id="pin-dob"
              type="date"
              className={cn("form-control form-control-sm", errors.dob && "border-red-500")}
              {...register("dob")}
            />
            {errors.dob && (
              <p className="mt-1 text-xs text-red-600">{errors.dob.message}</p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="pin-email" className="label f-weight-550">
              Email address
            </label>
            <input
              id="pin-email"
              type="email"
              placeholder="Email address"
              className={cn("form-control form-control-sm", errors.email && "border-red-500")}
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>
          <div className="form-group mb-4 flex flex-col gap-2">
            <SubmitButton className="btn btn-primary">Request new PIN</SubmitButton>
            <Link to="/account/login" className="hs-auth-link justify-center">
              Return to login
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
